import React from 'react'
import { MenuItem, Typography, Stack, Select } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import {
  collectionTranslationCompleteAction,
  collectionCloneAction,
} from 'src/reducers/course-collection/action'
import styles from './styles'
import { withStyles } from '@material-ui/core'
import { CoreIconButton } from 'src/components/core'
import {
  deleteCollection,
  patchCollectionLandingPageAction,
  collectionDuplicatingAction,
} from 'src/reducers/collection/action'
import MenuPopover from 'src/components/MenuPopover'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import { ButtonData, ButtonType, ButtonActionType } from 'src/model/button'
import useTrackerFunction from 'src/hooks/useTrackerFunction'
import { track } from '@amplitude/analytics-browser'
import EditCard from 'src/components/editor/components/custom-plugins/simple-box/course-list/edit-card'
import AccessRule from 'src/model/access-rule'
import GeneralSettings from 'src/components/user-access'
import TagPopover from 'src/pages/collections/components/tag-popover'
import CollectionTranslateModal from 'src/components/collection-translate-modal'
import UpgradePopup from 'src/components/new-upgrade-popup'
import Badge from 'src/components/user-access/components/badge'
import UserType from 'src/model/user-types'

const CollectionToolBar = props => {
  // `renderTrigger` replaces the default access chip + menu button; it gets
  // `{ openMenu, openAccess, accessLabel }`.
  const { collection, refresh, classes, renderTrigger } = props
  const history = useHistory()
  const dispatch = useDispatch()
  const [toolBarOpen, setToolBarOpen] = React.useState(null)
  const [tagAnchor, setTagAnchor] = React.useState(null)

  const { enqueueSnackbar } = useSnackbar()

  // Tracking functions
  const trackMenuOpen = useTrackerFunction('opened_collection_menu')
  const trackDetailClick = useTrackerFunction('clicked_collection_detail')
  const trackDeleteClick = useTrackerFunction('clicked_delete_collection')
  const trackDeleteConfirm = useTrackerFunction('confirmed_delete_collection')

  const handleOpen = event => {
    trackMenuOpen()
    setToolBarOpen(event.currentTarget)
  }

  const handleClose = () => {
    setToolBarOpen(null)
  }

  const handleEdit = () => {
    trackDetailClick()
    history.push(`/collection/${collection.id}`)
    handleClose()
  }

  // Change thumbnail function for collection
  const changeThumbnailCollection = collection => {
    track('collection_change_thumbnail_clicked')
    handleClose()

    // useRef benzeri bir yapı ile değeri tutun
    const modalDataRef = {
      current: {
        thumbnail: collection.thumbnail || collection.coverImageUrl || '',
      },
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => {
                dispatch(messageBoxHideAction())
              },
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Save',
              ButtonActionType.YES,
              () => {
                dispatch(
                  patchCollectionLandingPageAction(
                    collection.id,
                    [
                      {
                        path: 'thumbnail',
                        value: modalDataRef.current.thumbnail,
                      },
                    ],
                    () => {
                      // Callback after successful patch
                      dispatch(messageBoxHideAction())
                    },
                  ),
                )
              },
            ),
          ],
          title: 'Change thumbnail',
          component: (
            <EditCard
              thumbnailUrl={
                collection.thumbnail || collection.coverImageUrl || ''
              }
              onSave={settings => {
                modalDataRef.current = { ...settings }
              }}
              setImageURL={newUrl => {
                if (newUrl) {
                  modalDataRef.current.thumbnail = newUrl
                }
              }}
            />
          ),
          maxWidth: '680px',
        }),
      ),
    )
  }

  // Duplicate the collection (everything except learners and collaborators).
  // The backend creates the new collection shell immediately and clones its courses
  // in the background, so we mark the new clone as "duplicating" and refresh the list;
  // the badge clears when the CollectionDuplicationComplete SignalR event arrives.
  const duplicateCollection = () => {
    track('collection_duplicate_clicked')
    handleClose()
    dispatch(
      collectionCloneAction(collection.id, newCollection => {
        if (newCollection?.id) {
          dispatch(collectionDuplicatingAction(newCollection.id))
        }
        if (refresh && typeof refresh === 'function') {
          refresh()
        }
      }),
    )
  }

  const canTranslate = UserType.hasCourseTranslation(
    window.CONSTANTS.USER_ROLE_ID,
  )

  const showTranslateUpgrade = () => {
    track('upgrade_banner_seen', { type: 'course_translation' })
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          component: (
            <UpgradePopup
              upgradeButtonText={'Upgrade to Pro'}
              planName={'Pro'}
              upgradeDescription={
                <Typography fontSize={'14px'} fontWeight={400}>
                  Upgrade to the Pro Plan to translate your courses with AI and
                  access exclusive features.
                </Typography>
              }
              onClose={() => dispatch(messageBoxHideAction())}
            />
          ),
        }),
      ),
    )
  }

  const translateCollection = () => {
    track('collection_translate_clicked')
    handleClose()

    if (!canTranslate) {
      showTranslateUpgrade()
      return
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: 'Translate Learning Path',
          component: (
            <CollectionTranslateModal
              collectionId={collection.id}
              collectionName={collection.collectionName || collection.name}
              refresh={refresh}
            />
          ),
          maxWidth: '640px',
        }),
      ),
    )
  }

  const deleteButtonClickHandler = e => {
    trackDeleteClick()
    handleClose()
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [
            new ButtonData(ButtonType.NORMAL, 'No', ButtonActionType.NO, e => {
              dispatch(messageBoxHideAction())
            }),
            new ButtonData(
              ButtonType.ACTION,
              'Yes',
              ButtonActionType.DELETE,
              e => {
                trackDeleteConfirm()
                dispatch(
                  deleteCollection(collection.id, () => {
                    dispatch(messageBoxHideAction())
                  }),
                )
              },
            ),
          ],
          title: 'Delete Learning Path',
          subTitle: 'You are about to delete this learning path. Do you confirm?',
          description: null,
          note:
            'Note: Courses of this learning path will be deleted as well.',
          variant: MessageBoxVariant.NOTICE,
        }),
      ),
    )
  }
  const generalSettings = collection => {
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          maxWidth: '880px',
          maxHeight: '660px',
          buttons: [],
          title: 'Learners authentication',
          component: (
            <GeneralSettings
              collectionId={collection.id}
              collectionEditCard={collection}
              onSave={() => {
                // Refresh the list after saving
                if (refresh && typeof refresh === 'function') {
                  refresh()
                }
              }}
            />
          ),
        }),
      ),
    )
  }

  const accessLabel = collection.accessRule
    ? AccessRule[collection.accessRule]
    : 'Public'

  return (
    <div
      className={classes.collectionToolBar}
      onClick={e => e.stopPropagation()}
    >
      {renderTrigger ? (
        renderTrigger({
          openMenu: handleOpen,
          openAccess: () => generalSettings(collection),
          accessLabel,
        })
      ) : (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0.5}
          width="100%"
        >
          <div
            className={classes.accessRule}
            onClick={() => generalSettings(collection)}
          >
            <Icon icon="fluent:key-24-regular" width="11px" height="11px" />
            {collection.accessRule
              ? AccessRule[collection.accessRule]
              : 'Public'}
          </div>
          <CoreIconButton
            size="small"
            onClick={handleOpen}
            variant="text"
            color="secondary"
          >
            <Icon icon="eva:more-vertical-fill" width={20} height={20} />
          </CoreIconButton>
        </Stack>
      )}

      <MenuPopover
        open={Boolean(toolBarOpen)}
        anchorEl={toolBarOpen}
        onClose={handleClose}
        sx={{
          width: 'auto',
          '& .MuiMenuItem-root': {
            px: 1,
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
        disabledArrow
      >
        <MenuItem onClick={handleEdit}>
          <Icon
            icon="mdi:eye-outline"
            width={18}
            height={18}
            style={{ marginRight: 8, color: '#637381' }}
          />
          <Typography fontSize="14px" fontWeight={400}>
            Open
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            changeThumbnailCollection(collection)
          }}
        >
          <Icon
            icon="mdi:image-outline"
            width={18}
            height={18}
            style={{ marginRight: 8, color: '#637381' }}
          />
          <Typography fontSize="14px" fontWeight={400}>
            Change thumbnail
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const anchor = toolBarOpen
            setToolBarOpen(null)
            setTagAnchor(anchor)
          }}
        >
          <Icon
            icon="mdi:tag-outline"
            width={18}
            height={18}
            style={{ marginRight: 8, color: '#637381' }}
          />
          <Typography fontSize="14px" fontWeight={400}>
            Tag
          </Typography>
        </MenuItem>
        <MenuItem onClick={duplicateCollection}>
          <Icon
            icon="mdi:content-copy"
            width={18}
            height={18}
            style={{ marginRight: 8, color: '#637381' }}
          />
          <Typography fontSize="14px" fontWeight={400}>
            Duplicate
          </Typography>
        </MenuItem>
        <MenuItem onClick={translateCollection}>
          <Icon
            icon="mdi:translate"
            width={18}
            height={18}
            style={{ marginRight: 8, color: '#637381' }}
          />
          <Typography fontSize="14px" fontWeight={400}>
            Translate
          </Typography>
          <Badge type={2} />
        </MenuItem>
        <MenuItem onClick={deleteButtonClickHandler}>
          <Icon
            icon="mdi:trash-can-outline"
            width={18}
            height={18}
            style={{ marginRight: 8, color: '#d4351c' }}
          />
          <Typography
            fontSize="14px"
            fontWeight={400}
            sx={{ color: '#d4351c' }}
          >
            Delete
          </Typography>
        </MenuItem>
      </MenuPopover>

      <TagPopover
        anchorEl={tagAnchor}
        open={Boolean(tagAnchor)}
        onClose={() => setTagAnchor(null)}
        collection={collection}
      />
    </div>
  )
}

export default withStyles(styles)(CollectionToolBar)
